import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ApiBlocker, BlockerReply, BlockerResolution } from '../lib/blockers';
import type { KeyRow } from '../lib/keys-types';
import { absolute, ago } from '../lib/time';
import { BlockerAnswerForm } from './BlockerAnswerForm';
import { BlockerFixLink } from './BlockerFixLink';
import { BlockerGuidance } from './BlockerGuidance';
import { BlockerNeeds } from './BlockerNeeds';
import { BlockerWithdrawButton } from './BlockerWithdrawButton';
import { StatusPill, type StatusTone } from './StatusPill';

// One answerable hold. Presenter — the route owns the read, both writes and their
// refusals.
//
// A CLOSED ROW IS SHOWN, AND SHOWS NO CONTROLS. `expired` in particular is
// evidence rather than noise: it says the platform asked and nobody came, which
// is exactly what deleting the row would hide from the next identical ask.
//
// THE ANSWER TEXT IS NOT HERE and cannot be. `maskBlocker` omits it on purpose
// and serves `answered: boolean`; the row states that words were supplied, by
// whom, and which work they filed — never the words.

const TONE: Record<BlockerResolution, StatusTone> = {
  answered: 'success',
  withdrawn: 'neutral',
  expired: 'warning',
};

/** Where this hold came from — each coordinate shown only when the row carries it. */
export function blockerCoordinates(
  blocker: ApiBlocker,
): readonly { readonly label: string; readonly id: string }[] {
  return [
    { label: t('blockers.repo'), id: blocker.repo_full_name },
    { label: t('blockers.run'), id: blocker.run_id },
    { label: t('blockers.task'), id: blocker.task_id },
    { label: t('blockers.runner'), id: blocker.runner_id },
  ].flatMap((c) => (c.id === null ? [] : [{ label: c.label, id: c.id }]));
}

/** One blocker: its question, where it came from, and — while open — the answer controls. */
export function BlockerCard(props: {
  readonly blocker: ApiBlocker;
  /** True while THIS blocker has an answer or a withdraw in flight. */
  readonly busy: boolean;
  /** The account's keys, for the answer form's optional model field (#6521). */
  readonly keys?: readonly KeyRow[] | undefined;
  readonly onAnswer: (blocker: ApiBlocker, reply: BlockerReply) => void;
  readonly onWithdraw: (blocker: ApiBlocker) => void;
}): JSX.Element {
  const resolution = (): BlockerResolution | null => props.blocker.resolution;
  // `null` is the ONE open state (`entities/blocker.ts`), so it maps to the live
  // pill rather than a fourth entry in a table keyed by the closed vocabulary.
  const tone = (): StatusTone => {
    const closed = resolution();
    return closed === null ? 'live' : TONE[closed];
  };

  return (
    <section class="card blocker-card">
      {/* divs, not header/footer: outside sectioning content those map to
          banner/contentinfo and duplicate the shell's landmarks (axe). */}
      <div class="card__header card__header--row">
        <h2 class="card__title">{props.blocker.summary}</h2>
        <div class="blocker-card__badges">
          <span class="badge badge--outline">{t(`blockers.kind_${props.blocker.kind}`)}</span>
          <StatusPill tone={tone()}>
            {t(`blockers.resolution_${resolution() ?? 'open'}`)}
          </StatusPill>
        </div>
      </div>
      <div class="card__content">
        <p class="card__desc">{props.blocker.detail}</p>
        <p class="card__desc">
          {t('blockers.asked', { when: ago(props.blocker.asked_at_iso) })}
          <For each={blockerCoordinates(props.blocker)}>
            {(c) => (
              <>
                {' · '}
                {c.label} <span class="data-table__mono">{c.id}</span>
              </>
            )}
          </For>
        </p>

        <BlockerGuidance
          guidance={props.blocker.guidance}
          open={resolution() === null}
          offersChoices={props.blocker.options !== null}
        />

        <Show when={props.blocker.needs}>{(needs) => <BlockerNeeds needs={needs()} />}</Show>

        <Show
          when={resolution() === null}
          fallback={
            <>
              <Show when={props.blocker.answered_at_iso}>
                {(when) => (
                  <p class="card__desc">{t('blockers.closed_at', { when: absolute(when()) })}</p>
                )}
              </Show>
              <Show when={props.blocker.answered_by_user_id}>
                {(user) => (
                  <p class="card__desc">
                    <Show
                      when={props.blocker.answered_by_agent}
                      fallback={t('blockers.answered_by', { user: user() })}
                    >
                      {(agent) => t('blockers.answered_by_agent', { agent: agent(), user: user() })}
                    </Show>
                  </p>
                )}
              </Show>
              <Show when={props.blocker.raw_task_id}>
                {(id) => <p class="card__desc">{t('blockers.filed', { id: id() })}</p>}
              </Show>
              <Show when={props.blocker.answered}>
                <p class="card__desc">{t('blockers.answer_not_shown')}</p>
              </Show>
            </>
          }
        >
          <BlockerFixLink url={props.blocker.fix_url} />
          <BlockerAnswerForm
            blockerId={props.blocker.id}
            options={props.blocker.options}
            freeLabel={
              props.blocker.guidance?.kind === 'steps'
                ? props.blocker.guidance.free_label
                : undefined
            }
            offersDone={
              props.blocker.guidance?.kind === 'steps' && props.blocker.kind !== 'decision_needed'
            }
            keys={props.keys}
            filesAsk={props.blocker.answer_files_ask}
            busy={props.busy}
            onSubmit={(reply) => props.onAnswer(props.blocker, reply)}
          />
          <BlockerWithdrawButton
            busy={props.busy}
            onWithdraw={() => props.onWithdraw(props.blocker)}
          />
        </Show>
      </div>
    </section>
  );
}
